import React, { ReactNode } from "react";
import {
  KeyValuePairs,
  KeyValuePairsProps,
  Link,
  Popover,
  Spinner,
} from "@amzn/awsui-components-console";
import {
  FixedFeeConfig,
  FixedFeeType,
} from "@amzn/fuse-accounting-central-datastore-client";
import { NUMBER_CELL_WRAPPER } from "src/components/allFixedFeeConfigsPage/fixed-fee-config-attributes";
import { Domain } from "src/config/BartLambdaConfig";
import Decimal from "decimal.js";
import { getTransactionInfo } from "src/pages/commons/transaction-info";

export enum AdditionalInfoModalType {
  Issuance = "Issuance",
  AdjustmentSubBalanceShift = "Adjustment/SubBalanceShift",
  Matcher = "Matcher",
}

interface AdditionalInfoModalProps {
  fixedFeeConfig?: FixedFeeConfig;
  loading: boolean;
  child: ReactNode;
  domain: Domain;
  modalType: AdditionalInfoModalType;
}

const getTransactionItems = (
  externalId: string,
  transactionType: string,
  isTest: boolean,
  domain: Domain,
  fixedFeeType?: FixedFeeType,
) => {
  const transactionInfo = getTransactionInfo(
    externalId,
    transactionType,
    isTest,
    domain,
  );

  const items: { label: ReactNode; value: ReactNode }[] = [
    {
      label: "Transaction",
      value: (
        <Link external href={`/transactionsearch/external_id/${externalId}`}>
          {externalId}
        </Link>
      ),
    },
    {
      label: "FuseBillingService Workflows",
      value: (
        <span>
          <Link external href={transactionInfo.fuse.transactionWf?.link}>
            Transaction WF
          </Link>
          ,&nbsp;
          <Link external href={transactionInfo.fuse.fusenWf.link}>
            FUSEN WF
          </Link>
        </span>
      ),
    },
    {
      label: "Other Links",
      value: (
        <span>
          <Link external href={transactionInfo.acid.link}>
            ACID
          </Link>
          ,&nbsp;
          <Link external href={transactionInfo.flash.link}>
            FLASH
          </Link>
          {fixedFeeType === FixedFeeType.TERMINATION_AT_CONVENIENCE &&
            transactionInfo.race && (
              <>
                ,&nbsp;
                <Link external href={transactionInfo.race.link}>
                  rACE
                </Link>
              </>
            )}
        </span>
      ),
    },
  ];

  return items;
};

const getIssuance = (fixedFeeConfig: FixedFeeConfig, domain: Domain) => {
  const isTac =
    fixedFeeConfig.fixedFeeType === FixedFeeType.TERMINATION_AT_CONVENIENCE;

  // TAC: multiple periods (fixedFeeId-1.Payment, fixedFeeId-2.Payment, ...)
  if (isTac && fixedFeeConfig.tacBreakdown) {
    const issuanceItems: { label: ReactNode; value: ReactNode }[] = [
      {
        label: "Date",
        value: fixedFeeConfig.dealStartDate,
      },
    ];

    const sortedPeriods = Object.entries(fixedFeeConfig.tacBreakdown).sort(
      ([, a], [, b]) =>
        (a.periodStartDate ?? "").localeCompare(b.periodStartDate ?? ""),
    );

    sortedPeriods.forEach(([periodKey, period], index) => {
      const periodNumber = index + 1;
      const externalId = `${fixedFeeConfig.fixedFeeId}-${periodNumber}.Payment`;
      const transactionInfo = getTransactionInfo(
        externalId,
        "CHARGE",
        fixedFeeConfig.environment === "TEST",
        domain,
      );

      issuanceItems.push(
        {
          label: `Period ${periodNumber}`,
          value: `${period.periodStartDate} to ${period.periodEndDate}`,
        },
        {
          label: `Period ${periodNumber} Amount`,
          value: period.issuanceAmount
            ? Number(period.issuanceAmount.string).toLocaleString("en-US")
            : "-",
        },
        {
          label: `Period ${periodNumber} Transaction`,
          value: (
            <Link
              external
              href={`/transactionsearch/external_id/${externalId}`}
            >
              {externalId}
            </Link>
          ),
        },
        {
          label: `Period ${periodNumber} Links`,
          value: (
            <span>
              <Link external href={transactionInfo.acid.link}>
                ACID
              </Link>
              ,&nbsp;
              <Link external href={transactionInfo.flash.link}>
                FLASH
              </Link>
              {transactionInfo.race && (
                <>
                  ,&nbsp;
                  <Link external href={transactionInfo.race.link}>
                    rACE
                  </Link>
                </>
              )}
            </span>
          ),
        },
      );
    });

    return issuanceItems;
  }

  // Standard: single issuance (fixedFeeId.Payment)
  const issuanceItems: { label: ReactNode; value: ReactNode }[] = [
    {
      label: "Date",
      value: fixedFeeConfig.dealStartDate,
    },
  ];

  if (fixedFeeConfig.reclassBalance) {
    issuanceItems.push(
      {
        label: "Long-term Receivables",
        value: Number(
          fixedFeeConfig.reclassBalance.longTermReceivables!.string,
        ).toLocaleString("en-US"),
      },
      {
        label: "Long-term Deferred Revenue",
        value: Number(
          fixedFeeConfig.reclassBalance.longTermDeferredRevenue!.string,
        ).toLocaleString("en-US"),
      },
      {
        label: "Unbilled AR",
        value: Number(
          fixedFeeConfig.reclassBalance.unbilledAccountsReceivables!.string,
        ).toLocaleString("en-US"),
      },
      {
        label: "Unearned Revenue",
        value: Number(
          fixedFeeConfig.reclassBalance.unearnedRevenue!.string,
        ).toLocaleString("en-US"),
      },
    );
  } else {
    issuanceItems.push({
      label: "Amount",
      value: Number(fixedFeeConfig.contractPrice!.string).toLocaleString(
        "en-US",
      ),
    });
  }

  issuanceItems.push(
    ...getTransactionItems(
      `${fixedFeeConfig.fixedFeeId}.Payment`,
      "CHARGE",
      fixedFeeConfig.environment === "TEST",
      domain,
    ),
  );

  return issuanceItems;
};

const getMostRecentAdjustment = (
  fixedFeeConfig: FixedFeeConfig,
  mostRecentOccurrenceDate: string,
  mostRecentOccurrenceIndex: number,
  domain: Domain,
) => {
  const mostRecentOccurrenceResult =
    fixedFeeConfig.adjustments![mostRecentOccurrenceDate];

  const mostRecentAdjustmentItems: { label: ReactNode; value: ReactNode }[] = [
    {
      label: "Date",
      value: mostRecentOccurrenceDate,
    },
    {
      label: "Adjusting Period",
      value:
        mostRecentOccurrenceResult.periodStartDate +
        " to " +
        mostRecentOccurrenceResult.periodEndDate,
    },
    {
      label: "Queried Activations/Renewals",
      value:
        mostRecentOccurrenceResult.activeBenefitCount?.toLocaleString("en-US"),
    },
    {
      label: "Queried Deferral Revenue",
      value: NUMBER_CELL_WRAPPER(mostRecentOccurrenceResult.deferredRevenue),
    },
    {
      label: "Revised Plan Price",
      value: NUMBER_CELL_WRAPPER(mostRecentOccurrenceResult.revisedPlanPrice),
    },
  ];

  const adjustmentAmount = Number(
    mostRecentOccurrenceResult.adjustmentAmount!.string,
  );
  if (adjustmentAmount === 0) {
    mostRecentAdjustmentItems.push({
      label: "Amount",
      value: "0",
    });
  } else {
    const isTrueUp = adjustmentAmount > 0;
    mostRecentAdjustmentItems.push({
      label: "Amount",
      value: isTrueUp ? (
        <span style={{ color: "#00802f" }}>
          {adjustmentAmount.toLocaleString("en-US")} (True-Up)
        </span>
      ) : (
        <span style={{ color: "#db0000" }}>
          {(-adjustmentAmount).toLocaleString("en-US")} (True-Down)
        </span>
      ),
    });

    mostRecentAdjustmentItems.push(
      ...getTransactionItems(
        `${fixedFeeConfig.fixedFeeId}-${mostRecentOccurrenceIndex}.${isTrueUp ? "TrueUp" : "TrueDown"}`,
        "TRANSACTION",
        fixedFeeConfig.environment === "TEST",
        domain,
      ),
    );
  }
  return mostRecentAdjustmentItems;
};

const getMostRecentSubBalanceShift = (
  fixedFeeConfig: FixedFeeConfig,
  mostRecentOccurrenceDate: string,
  mostRecentOccurrenceIndex: number,
  domain: Domain,
) => {
  const mostRecentOccurrenceResult =
    fixedFeeConfig.adjustments![mostRecentOccurrenceDate];

  let longTermReceivables = new Decimal(
    fixedFeeConfig.reclassBalance!.longTermReceivables!.string,
  );
  let longTermDeferredRevenue = new Decimal(
    fixedFeeConfig.reclassBalance!.longTermDeferredRevenue!.string,
  );
  const contractPrice = new Decimal(fixedFeeConfig.contractPrice!.string);

  Object.values(fixedFeeConfig.adjustments!).forEach((occurrenceResult) => {
    if (occurrenceResult.receivableShiftAmount) {
      longTermReceivables = longTermReceivables.sub(
        new Decimal(occurrenceResult.receivableShiftAmount!.string),
      );
    }
    if (occurrenceResult.deferralRevenueShiftAmount) {
      longTermDeferredRevenue = longTermDeferredRevenue.sub(
        new Decimal(occurrenceResult.deferralRevenueShiftAmount!.string),
      );
    }
  });

  const mostRecentSubBalanceShiftItems: {
    label: ReactNode;
    value: ReactNode;
  }[] = [
    {
      label: "Long-term AR (After)",
      value: NUMBER_CELL_WRAPPER(longTermReceivables),
    },
    {
      label: "Long-term Deferred Revenue (After)",
      value: NUMBER_CELL_WRAPPER(longTermDeferredRevenue),
    },
    {
      label: "Short-term AR (After)",
      value: NUMBER_CELL_WRAPPER(contractPrice.sub(longTermReceivables)),
    },
    {
      label: "Short-term Deferred-Revenue (After)",
      value: NUMBER_CELL_WRAPPER(contractPrice.sub(longTermDeferredRevenue)),
    },
  ];

  const receivableShiftAmount = Number(
    mostRecentOccurrenceResult.receivableShiftAmount!.string,
  );
  const deferredRevenueShiftAmount = Number(
    mostRecentOccurrenceResult.deferralRevenueShiftAmount!.string,
  );
  if (receivableShiftAmount === 0) {
    mostRecentSubBalanceShiftItems.push({
      label: "Receivable Shift Amount",
      value: "0",
    });
  } else {
    mostRecentSubBalanceShiftItems.push({
      label: "Receivable Shift Amount",
      value:
        receivableShiftAmount > 0 ? (
          <span style={{ color: "#00802f" }}>
            {receivableShiftAmount.toLocaleString("en-US")}
          </span>
        ) : (
          <span style={{ color: "#db0000" }}>
            {(-receivableShiftAmount).toLocaleString("en-US")}
          </span>
        ),
    });
  }

  if (deferredRevenueShiftAmount === 0) {
    mostRecentSubBalanceShiftItems.push({
      label: "Deferred Revenue Shift Amount",
      value: "0",
    });
  } else {
    mostRecentSubBalanceShiftItems.push({
      label: "Deferred Revenue Shift Amount",
      value:
        deferredRevenueShiftAmount > 0 ? (
          <span style={{ color: "#00802f" }}>
            {deferredRevenueShiftAmount.toLocaleString("en-US")}
          </span>
        ) : (
          <span style={{ color: "#db0000" }}>
            {(-deferredRevenueShiftAmount).toLocaleString("en-US")}
          </span>
        ),
    });
  }

  if (receivableShiftAmount != 0 || deferredRevenueShiftAmount != 0) {
    mostRecentSubBalanceShiftItems.push(
      ...getTransactionItems(
        `${fixedFeeConfig.fixedFeeId}-${mostRecentOccurrenceIndex}.SubBalShift`,
        "CHARGE",
        fixedFeeConfig.environment === "TEST",
        domain,
      ),
    );
  }

  return mostRecentSubBalanceShiftItems;
};

const getMatcher = (fixedFeeConfig: FixedFeeConfig, domain: Domain) => {
  // for FixedFee matcher:
  // - match key: the fixedFeeId
  // - account: 23200 (Unearned Revenue), if with reclass enabled, additionally: 18203 (LT Receivables) and 28655 (LT Deferred Revenue)

  const matcherItems: { label: ReactNode; value: ReactNode }[] = [];

  const accounts: Record<string, string> = {
    "23200": "Unearned Revenue",
  };

  if (fixedFeeConfig.reclassBalance) {
    accounts["18203"] = "Long-term Receivables";
    accounts["28655"] = "Long-term Deferred Revenue";
  }

  const transactionInfo = getTransactionInfo(
    `${fixedFeeConfig.fixedFeeId}.Payment`,
    "CHARGE",
    fixedFeeConfig.environment === "TEST",
    domain,
    {
      matcher: {
        matchKey: fixedFeeConfig.fixedFeeId!,
        accounts: new Set(Object.keys(accounts)),
      },
    },
  );

  if (transactionInfo.matcher) {
    Object.entries(transactionInfo.matcher).forEach(
      ([account, matcherLink]) => {
        matcherItems.push({
          label: `${accounts[account]}`,
          value: (
            <Link external href={matcherLink}>
              {account}
            </Link>
          ),
        });
      },
    );
  }

  return matcherItems;
};

const AdditionalInfoPopover: React.FC<AdditionalInfoModalProps> = ({
  fixedFeeConfig,
  loading,
  child,
  domain,
  modalType,
}) => {
  let content;

  if (loading) {
    content = <Spinner size="large" />;
  } else if (!fixedFeeConfig) {
    content = <div>Something went wrong, please try again.</div>;
  } else if (modalType === AdditionalInfoModalType.Issuance) {
    const issuance: KeyValuePairsProps.Group = {
      type: "group",
      title: "Issuance",
      items: getIssuance(fixedFeeConfig, domain),
    };
    content = <KeyValuePairs columns={1} items={[issuance]} />;
  } else if (modalType === AdditionalInfoModalType.AdjustmentSubBalanceShift) {
    if (
      !fixedFeeConfig.adjustments ||
      Object.keys(fixedFeeConfig.adjustments).length === 0
    ) {
      content = <div>There are no adjustment records.</div>;
    } else {
      const occurrenceResults = fixedFeeConfig.adjustments;
      const occurrenceDates = Object.keys(occurrenceResults);
      const executedOccurrenceDates = occurrenceDates.filter(
        (occurrenceDate) => {
          return (
            occurrenceResults[occurrenceDate].adjustmentAmount !== undefined
          );
        },
      );

      const adjustmentSummary: KeyValuePairsProps.Pair = {
        type: "pair",
        label: "Executed Occurrences (to date)",
        value: `${executedOccurrenceDates.length} out of ${occurrenceDates.length}`,
      };

      if (executedOccurrenceDates.length === 0) {
        content = <KeyValuePairs columns={1} items={[adjustmentSummary]} />;
      } else {
        const mostRecentOccurrenceDate = executedOccurrenceDates.reduce(
          (a, b) => (a > b ? a : b),
        );

        const mostRecentAdjustment: KeyValuePairsProps.Group = {
          type: "group",
          title: "Most Recent Adjustment",
          items: [
            {
              label: adjustmentSummary.label,
              value: adjustmentSummary.value,
            },
            ...getMostRecentAdjustment(
              fixedFeeConfig,
              mostRecentOccurrenceDate,
              executedOccurrenceDates.length,
              domain,
            ),
          ],
        };
        content = [mostRecentAdjustment];

        if (
          occurrenceResults[mostRecentOccurrenceDate].deferralRevenueShiftAmount
        ) {
          const mostRecentSubBalanceShift: KeyValuePairsProps.Group = {
            type: "group",
            title: "Most Recent SubBalanceShift",
            items: getMostRecentSubBalanceShift(
              fixedFeeConfig,
              mostRecentOccurrenceDate,
              executedOccurrenceDates.length,
              domain,
            ),
          };
          content.push(mostRecentSubBalanceShift);
        }

        content = <KeyValuePairs columns={content.length} items={content} />;
      }
    }
  } else {
    const matcher: KeyValuePairsProps.Group = {
      type: "group",
      title: "Matcher",
      items: getMatcher(fixedFeeConfig, domain),
    };
    content = <KeyValuePairs columns={1} items={[matcher]} />;
  }

  return (
    <Popover
      renderWithPortal
      fixedWidth
      header={
        modalType === AdditionalInfoModalType.AdjustmentSubBalanceShift
          ? `Most Recent ${modalType} Info`
          : `${modalType} Info`
      }
      size="large"
      triggerType="custom"
      content={content}
    >
      {child}
    </Popover>
  );
};

export default AdditionalInfoPopover;
